import type { MonacoDiffEditor } from '@monaco-editor/react';
import { DiffEditor, Editor } from '@monaco-editor/react';
import { CloudUpload } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import type { AlertColor, SnackbarCloseReason } from '@mui/material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'material-react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getExperiment, updateExperiment } from '../../services/experiments';
import { experimentsUIUrl } from '../../variables/Pages';

function EditComponent({ experiment, setExperiment, originalExperiment }: any) {
  const [openDialog, setOpenDialog] = React.useState(false);
  const [showDiff, setShowDiff] = React.useState(false);
  const [uploadSnackbarOpen, setUploadSnackbarOpen] = React.useState(false);
  const [uploadSnackbarMessage, setUploadSnackbarMessage] = React.useState('');
  const [uploadSnackbarSeverity, setUploadSnackbarSeverity] =
    React.useState<AlertColor>('success');
  const [openDragDropModal, setOpenDragDropModal] = React.useState(false);
  const navigate = useNavigate();

  const handleOpenDragDropModal = () => {
    setOpenDragDropModal(true);
  };

  const handleCloseDragDropModal = () => {
    setOpenDragDropModal(false);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  const convertToUTC = (datetime: Date) => {
    return new Date(
      Date.UTC(
        datetime.getFullYear(),
        datetime.getMonth(),
        datetime.getDate(),
        datetime.getHours(),
        datetime.getMinutes(),
        datetime.getSeconds(),
      ),
    );
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const randomizedSchedule = JSON.parse(content.replace(/\\"/g, '"'));

        const mappedRandomizedScheduleValue: any = {};
        const mappedMfcs: any[] = [];
        const randomizedScheduleGrouped: { [key: string]: any[] } = {};

        randomizedSchedule.forEach((item: any) => {
          if (!randomizedScheduleGrouped[item.randomization_unit_id]) {
            randomizedScheduleGrouped[item.randomization_unit_id] = [];
          }
          randomizedScheduleGrouped[item.randomization_unit_id]!.push(item);
        });

        const groupedValues = Object.values(randomizedScheduleGrouped);

        mappedRandomizedScheduleValue.name =
          groupedValues[0]![0].experiment_name;
        mappedRandomizedScheduleValue.id = groupedValues[0]![0].experiment_id;

        groupedValues.forEach((mfcEntry) => {
          const mappedMfcItem: any = {};
          mappedMfcItem.mfcId = mfcEntry[0].randomization_unit_id;
          const mappedMfcDurations: any[] = [];

          mfcEntry.forEach((mfcDuration) => {
            const mappedMfcDurationItem: any = {};
            mappedMfcDurationItem.enabled = mfcDuration.treatment !== 'control';
            mappedMfcDurationItem.startTime = convertToUTC(
              new Date(mfcDuration.start_time_utc),
            ).toISOString();
            mappedMfcDurationItem.endTime = convertToUTC(
              new Date(mfcDuration.end_time_utc),
            ).toISOString();
            mappedMfcDurations.push(mappedMfcDurationItem);
          });

          mappedMfcItem.durations = mappedMfcDurations;
          mappedMfcs.push(mappedMfcItem);
        });

        mappedRandomizedScheduleValue.mfcDurations = mappedMfcs;

        const experimentJson = JSON.parse(experiment);
        if (!experimentJson.experiment.randomizedSchedules) {
          experimentJson.experiment.randomizedSchedules = [];
        }
        experimentJson.experiment.randomizedSchedules.push(
          mappedRandomizedScheduleValue,
        );

        setExperiment(JSON.stringify(experimentJson, null, 2));
        setUploadSnackbarMessage('File was uploaded successfully');
        setUploadSnackbarSeverity('success');
        setUploadSnackbarOpen(true); // Show snackbar on successful upload
        handleCloseDragDropModal(); // Dismiss modal on successful upload
      } catch (error) {
        toast.error(`Error parsing JSON: ${error}`);
        setUploadSnackbarMessage('Error parsing JSON file');
        setUploadSnackbarSeverity('error');
        setUploadSnackbarOpen(true); // Show snackbar on upload failure
      }
    };
    reader.readAsText(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleRevertChanges = () => {
    setExperiment(originalExperiment);
    setOpenDialog(false);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleToggleDiff = () => {
    setShowDiff(!showDiff);
  };

  const handleClickHere = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setUploadSnackbarOpen(false);
  };

  // DiffEditor does not have an onChange event, so we need to use the onMount event to get the modified editor
  const handleDiffEditorMount = (editor: MonacoDiffEditor) => {
    const modifiedEditor = editor.getModifiedEditor();
    modifiedEditor.onDidChangeModelContent(() => {
      setExperiment(modifiedEditor.getValue());
    });
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Container sx={{ maxWidth: '1200px', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            marginUp: '10px',
            width: '100%',
          }}
        >
          <Button
            onClick={() => navigate(experimentsUIUrl)}
            startIcon={<ArrowBackIcon />}
            variant='outlined'
          >
            Back to Experiment List
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={showDiff}
                color='primary'
                onChange={handleToggleDiff}
              />
            }
            label={<Typography color='textPrimary'>Use Diff Editor</Typography>}
            sx={{ marginLeft: '10px' }}
          />
          <Box>
            <Button
              onClick={handleOpenDragDropModal}
              sx={{ marginRight: '10px' }}
              variant='contained'
            >
              Add Switchback Schedule
            </Button>
            <Button
              disabled={originalExperiment === experiment}
              onClick={handleOpenDialog}
              sx={{
                color: originalExperiment === experiment ? 'gray' : 'secondary',
              }}
              variant='outlined'
            >
              Revert Changes
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'primary.main',
            color: 'white',
            textAlign: 'center',
          }}
        >
          <Typography variant='h6'>Edit Mode</Typography>
        </Box>
        <Dialog
          fullWidth
          aria-describedby='drag-drop-dialog-description'
          aria-labelledby='drag-drop-dialog-title'
          maxWidth='md'
          onClose={handleCloseDragDropModal}
          open={openDragDropModal}
        >
          <DialogTitle id='drag-drop-dialog-title'>
            Add Switchback Schedule
            <Button
              aria-label='close'
              onClick={handleCloseDragDropModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </Button>
          </DialogTitle>
          <DialogContent>
            <Box
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                border: '2px dashed #ccc',
                borderRadius: '4px',
                width: '100%',
              }}
            >
              <CloudUpload color='action' fontSize='large' />
              <Typography
                color='textSecondary'
                sx={{ marginTop: '10px' }}
                variant='subtitle1'
              >
                Drag & Drop a Switchback Schedule here
              </Typography>
              <Button
                onClick={handleClickHere}
                sx={{ marginTop: '10px' }}
                variant='contained'
              >
                Select a file
                <VisuallyHiddenInput
                  ref={fileInputRef}
                  multiple
                  onChange={handleFileChange}
                  type='file'
                />
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
        {showDiff ? (
          <DiffEditor
            height='90vh'
            language='json'
            modified={experiment}
            keepCurrentOriginalModel={true}
            keepCurrentModifiedModel={true}
            onMount={handleDiffEditorMount}
            options={{ diffAlgorithm: 'legacy' }}
            original={originalExperiment}
            theme='vs-dark'
            width='100%'
          />
        ) : (
          <Editor
            defaultLanguage='json'
            defaultValue={experiment}
            height='90vh'
            onChange={(v) => setExperiment(v)}
            theme='vs-dark'
            value={experiment}
            width='100%'
          />
        )}
        <Dialog
          aria-describedby='alert-dialog-description'
          aria-labelledby='alert-dialog-title'
          onClose={handleCloseDialog}
          open={openDialog}
        >
          <DialogTitle id='alert-dialog-title'>Revert Changes</DialogTitle>
          <DialogContent>
            <DialogContentText id='alert-dialog-description'>
              Are you sure you want to revert all changes? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button color='primary' onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button autoFocus color='primary' onClick={handleRevertChanges}>
              Revert
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          autoHideDuration={3000}
          onClose={handleUploadSnackbarClose}
          open={uploadSnackbarOpen}
        >
          <Alert
            onClose={handleUploadSnackbarClose}
            severity={uploadSnackbarSeverity}
            sx={{ width: '100%' }}
          >
            {uploadSnackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}

function VerifyComponent({ originalExperiment, experiment }: any) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Container sx={{ maxWidth: '1200px', width: '100%' }}>
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'primary.light',
            color: 'white',
            textAlign: 'center',
            mt: 6,
          }}
        >
          <Typography variant='h6'>Read-Only Mode</Typography>
        </Box>
        <DiffEditor
          height='90vh'
          language='json'
          modified={experiment}
          options={{ readOnly: true, diffAlgorithm: 'legacy' }}
          original={originalExperiment}
          keepCurrentOriginalModel={true}
          keepCurrentModifiedModel={true}
          theme='vs-dark'
          width='100%'
        />
      </Container>
    </Box>
  );
}

function ConfirmComponent({ originalExperiment, experiment }: any) {
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  const [message, setMessage] = React.useState('');
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] =
    React.useState<AlertColor>('info');

  const [open, setOpen] = React.useState(false);
  useEffect(() => {
    setOpen(false);
    setMessage('');

    return () => {
      setOpen(false);
      setMessage('');
    };
  }, [id]);

  const handleClick = async () => {
    const response = await updateExperiment(experiment, message);
    if (response.status === 200) {
      setSnackbarMessage('Success!');
      setSnackbarSeverity('success');
      setOpen(true);
      setTimeout(() => {
        navigate(`${experimentsUIUrl}/${id}`);
      }, 500);
    } else {
      setSnackbarSeverity('error');
      setSnackbarMessage(
        `[${response.status}] ${JSON.stringify(
          await response.json(),
          null,
          2,
        )}`,
      );
      setOpen(true);
    }
  };
  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Card
        raised={false}
        sx={{
          width: '50%',
          margin: '0 auto 10px auto',
          minHeight: '150px',
          maxWidth: '1200px',
        }}
      >
        <CardContent sx={{ width: '80%', margin: '0 auto' }}>
          <TextField
            fullWidth
            multiline
            label='Enter a description of the changes'
            onChange={(v) => setMessage(v.target.value)}
            value={message}
            variant='filled'
          />
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Button
            disabled={originalExperiment === experiment}
            onClick={handleClick}
            variant='contained'
          >
            {originalExperiment === experiment
              ? 'No changes made'
              : 'Submit changes'}
          </Button>
        </CardActions>
        <Snackbar
          disableWindowBlurListener
          autoHideDuration={5000}
          onClose={handleClose}
          open={open}
          sx={{ width: '80%', mt: 2 }}
        >
          <Alert
            onClose={handleClose}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
            variant='filled'
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Card>
    </Box>
  );
}

export default function ExperimentEdit() {
  const { id } = useParams<'id'>();
  const [originalExperiment, setOriginalExperiment] = React.useState<any>(null);
  const [experiment, setExperiment] = React.useState<any>(null);

  const steps: EditStep[] = [
    {
      index: 0,
      label: 'Edit Experiment',
      element: EditComponent({ experiment, setExperiment, originalExperiment }),
    },
    {
      index: 1,
      label: 'Verify Changes',
      element: VerifyComponent({ originalExperiment, experiment }),
    },
    {
      index: 2,
      label: 'Confirm Changes',
      element: ConfirmComponent({ originalExperiment, experiment }),
    },
  ];
  const [activeStep, setActiveStep] = React.useState(0);

  useEffect(() => {
    const fetchExperiment = async () => {
      try {
        if (id) {
          const result = await getExperiment(id);
          setExperiment(JSON.stringify(result, null, 2));
          setOriginalExperiment(JSON.stringify(result, null, 2));
        }
      } catch (error) {
        toast.error(`Error fetching experiment: ${error}`);
      }
    };

    fetchExperiment();
  }, [id]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step: number) => () => {
    setActiveStep(step);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ maxWidth: '1200px', width: '100%', marginTop: '10px' }}>
        <Stepper activeStep={activeStep}>
          {steps.map((step, index) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: {
              optional?: React.ReactNode;
              onClick?: () => void;
            } = {};
            labelProps.onClick = handleStepClick(index);
            return (
              <Step key={step.label} {...stepProps}>
                <StepLabel {...labelProps} style={{ cursor: 'pointer' }}>
                  {step.label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>

        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button
            disabled={activeStep === steps.length - 1}
            onClick={handleNext}
          >
            Next
          </Button>
        </Box>
        {steps[activeStep]?.element}
      </Box>
    </Box>
  );
}

interface EditStep {
  index: number;
  label: string;
  element: JSX.Element;
}
